import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/auth";
import NexWordmark from "@/components/NexWordmark";
import { LIFE_LOGO } from "@/lib/assets";
import { ArrowLeft, Check, X, DollarSign, Loader2, ExternalLink, Copy, CheckCheck } from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, value, mono = false }) => (
  <div>
    <div className="mono-body text-[10px] uppercase tracking-[0.2em] text-nex-muted mb-1">{label}</div>
    <div className={`text-nex-silver text-sm ${mono ? "font-mono" : ""} whitespace-pre-wrap`}>{value || <span className="text-nex-muted">—</span>}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="card-industrial p-6">
    <div className="mono-label mb-4">{title}</div>
    <div className="grid md:grid-cols-2 gap-5">{children}</div>
  </div>
);

const BriefDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fee, setFee] = useState(499);
  const [notes, setNotes] = useState("");
  const [acting, setActing] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/admin/briefs/${id}`);
      setBrief(data);
      setFee(data.consultation_fee || 499);
      setNotes(data.notes || "");
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const saveNotes = async () => {
    try {
      const { data } = await apiClient.patch(`/admin/briefs/${id}`, { notes });
      setBrief(data);
      toast.success("Notes saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markReviewed = async () => {
    try {
      const { data } = await apiClient.patch(`/admin/briefs/${id}`, { status: "reviewed" });
      setBrief(data);
      toast.success("Marked as reviewed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const accept = async () => {
    if (!window.confirm(`Accept this brief and email ${brief.full_name} the $${fee} consultation fee link?`)) return;
    setActing(true);
    try {
      const { data } = await apiClient.post(`/admin/briefs/${id}/accept`, {
        consultation_fee: Number(fee),
        origin_url: window.location.origin,
      });
      setCheckoutUrl(data.checkout_url);
      toast.success(data.email_sent ? "Accepted! Payment link emailed to client." : "Accepted — payment link generated.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActing(false);
    }
  };

  const reject = async () => {
    if (!window.confirm("Mark this brief as rejected?")) return;
    setActing(true);
    try {
      await apiClient.post(`/admin/briefs/${id}/reject`);
      toast.success("Marked as rejected");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActing(false);
    }
  };

  const copyLink = () => {
    if (!checkoutUrl) return;
    navigator.clipboard.writeText(checkoutUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading || !brief) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0F19]">
        <div className="mono-body text-[11px] uppercase tracking-[0.3em] text-nex-gold animate-pulse-gold">Loading brief...</div>
      </div>
    );
  }

  const status = brief.status || "new";
  const canAccept = ["new", "reviewed"].includes(status);
  const canReject = status !== "rejected" && status !== "paid";

  return (
    <div className="min-h-screen bg-[#0B0F19]">
      {/* Top bar */}
      <div className="border-b border-white/10 bg-[#0A0E17] sticky top-0 z-40 backdrop-blur-xl">
        <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-4">
            <img src={LIFE_LOGO} alt="" className="h-9 w-9 object-contain" />
            <div className="w-px h-8 bg-nex-border" />
            <NexWordmark size="text-xl" />
          </Link>
          <Link to="/admin" data-testid="back-to-dashboard" className="tick-btn-secondary text-xs !py-2 !px-3">
            <ArrowLeft size={13} /> Dashboard
          </Link>
        </div>
      </div>

      <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 py-10 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start gap-4">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="mono-label">// {brief.engine_recommendation} · Score {brief.friction_score}/100</span>
              <span className="mono-body text-[10px] uppercase tracking-widest px-2 py-1 border border-nex-gold/50 text-nex-gold bg-nex-gold/5">
                {status}
              </span>
            </div>
            <h1 className="serif-head text-4xl md:text-5xl">{brief.business_name}</h1>
            <div className="mono-body text-[12px] mt-2">
              {brief.full_name} · <a href={`mailto:${brief.email}`} className="text-nex-gold hover:underline">{brief.email}</a> · {brief.phone || "no phone"}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {status === "new" && (
              <button onClick={markReviewed} data-testid="mark-reviewed-btn" className="tick-btn-secondary !py-2">
                Mark Reviewed
              </button>
            )}
            {canReject && (
              <button onClick={reject} disabled={acting} data-testid="reject-btn" className="tick-btn-secondary !py-2 !border-red-500/40 !text-red-400 hover:!bg-red-500/10">
                <X size={13} /> Reject
              </button>
            )}
          </div>
        </div>

        {/* Accept panel */}
        {canAccept && (
          <div className="card-industrial corner-tick p-6 border-nex-gold/40">
            <div className="flex flex-col md:flex-row md:items-end gap-6">
              <div className="flex-1">
                <div className="mono-label mb-2">// Accept &amp; Send Consultation Link</div>
                <p className="text-nex-chrome text-sm">Setting the fee here creates a Stripe Checkout session and emails the payment link directly to {brief.full_name}. Once paid, the brief flips to <span className="text-emerald-300">PAID</span>.</p>
              </div>
              <div className="flex items-end gap-3">
                <div>
                  <label className="mono-label block mb-2">Consultation Fee (USD)</label>
                  <div className="relative">
                    <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-nex-gold" />
                    <input
                      data-testid="fee-input"
                      type="number"
                      value={fee}
                      onChange={(e) => setFee(e.target.value)}
                      min={1}
                      className="input-industrial pl-9 w-32 !py-3"
                    />
                  </div>
                </div>
                <button
                  onClick={accept}
                  disabled={acting || !fee || Number(fee) < 1}
                  data-testid="accept-btn"
                  className="tick-btn-primary disabled:opacity-40"
                >
                  {acting ? <><Loader2 size={16} className="animate-spin" /> Working...</> : <><Check size={16} /> Accept &amp; Send</>}
                </button>
              </div>
            </div>
            {checkoutUrl && (
              <div className="mt-5 p-4 bg-[#0B0F19] border border-emerald-500/30">
                <div className="mono-body text-[10px] uppercase tracking-widest text-emerald-300 mb-2">✓ Checkout link generated · already emailed to client</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 mono-body text-[11px] text-nex-silver overflow-x-auto whitespace-nowrap">{checkoutUrl}</code>
                  <button onClick={copyLink} data-testid="copy-checkout-btn" className="tick-btn-secondary !py-1.5 !px-3">
                    {copied ? <><CheckCheck size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                  </button>
                  <a href={checkoutUrl} target="_blank" rel="noreferrer" className="tick-btn-secondary !py-1.5 !px-3">
                    <ExternalLink size={12} /> Open
                  </a>
                </div>
              </div>
            )}
          </div>
        )}

        {status === "paid" && (
          <div className="card-industrial p-5 border-emerald-500/40 bg-emerald-500/5">
            <div className="mono-label !text-emerald-300 mb-1">// PAYMENT CONFIRMED</div>
            <p className="text-nex-silver text-sm">Client has paid the ${brief.consultation_fee || 499} consultation fee. Ready to build their prototype — <Link to="/admin/previews" className="text-nex-gold underline">create a preview slot →</Link></p>
          </div>
        )}

        {/* Sections */}
        <Section title="// Business Identity">
          <Field label="Industry" value={brief.industry} />
          <Field label="Team Size" value={brief.team_size} />
          <Field label="Years in Business" value={brief.years_in_business} />
          <Field label="Website" value={brief.website} />
          <Field label="Service Area" value={brief.service_area} />
          <Field label="Best Call Time" value={brief.best_call_time} />
        </Section>

        <Section title="// Current Tech Stack">
          <Field label="Communication" value={(brief.comms_tools || []).join(", ")} />
          <Field label="Scheduling" value={(brief.scheduling_tools || []).join(", ")} />
          <Field label="Invoicing / Payments" value={(brief.invoicing_tools || []).join(", ")} />
          <Field label="CRM" value={(brief.crm_tools || []).join(", ")} />
          <Field label="Inventory" value={(brief.inventory_tools || []).join(", ")} />
          <Field label="Reporting" value={(brief.reporting_tools || []).join(", ")} />
          <div className="md:col-span-2">
            <Field label="Frustrations" value={brief.tech_frustrations} />
          </div>
        </Section>

        <Section title="// Bottlenecks">
          <div className="md:col-span-2">
            <Field label="Primary Bottleneck" value={brief.bottleneck} />
          </div>
          <Field label="Secondary" value={brief.secondary_bottleneck} />
          <Field label="Where Data Slips" value={brief.data_leakage} />
          <Field label="Frequency" value={brief.bottleneck_frequency} />
          <Field label="Cost / Revenue Impact" value={brief.revenue_impact} />
        </Section>

        <Section title="// Goals &amp; Vision">
          <div className="md:col-span-2">
            <Field label="90-Day Success" value={brief.success_90_days} />
          </div>
          <Field label="Key KPIs" value={(brief.key_kpis || []).join(", ")} />
          <Field label="12-Month Growth Goal" value={brief.growth_goals_12mo} />
          <div className="md:col-span-2">
            <Field label="Required Integrations" value={brief.required_integrations} />
          </div>
        </Section>

        <Section title="// Timing &amp; Engagement">
          <Field label="Timeline" value={brief.timeline} />
          <Field label="Budget Tier" value={brief.budget_tier} />
          <Field label="Preferred Engagement" value={brief.preferred_engagement} />
        </Section>

        {/* Notes */}
        <div className="card-industrial p-6">
          <div className="mono-label mb-3">// Private Notes (owner only)</div>
          <textarea
            data-testid="notes-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            placeholder="Notes on this lead, follow-up context, red flags, ideal engine architecture..."
            className="input-industrial resize-none"
          />
          <div className="flex justify-end mt-3">
            <button onClick={saveNotes} data-testid="save-notes-btn" className="tick-btn-secondary !py-2 text-xs">
              Save Notes
            </button>
          </div>
        </div>

        <div className="mono-body text-[10px] text-nex-muted text-center pt-6">
          REF {brief.id?.slice(0, 8).toUpperCase()} · submitted {new Date(brief.submitted_at).toLocaleString()}
        </div>
      </div>
    </div>
  );
};

export default BriefDetail;
